'use es6';

import { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import emptyFunction from 'react-utils/emptyFunction';
import EPBreakpointControls from 'editor-components/header/EPBreakpointControls';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { selectBreakpoint } from 'ContentEditorUI/redux/actions/styleBreakpointsActions';
import { getActiveBreakpointPreviewWidth, getIsInDefaultBreakpointMode, getResponsiveBreakpoints } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { getActiveSidebarPanel } from 'ContentEditorUI/redux/selectors/sidebarSelectors';
import { SIDEBAR_PANELS } from 'ContentEditorUI/lib/sidebar/constants';
import iconNames from 'icons/iconNames';
import { getPreviewIframeNeedsRefresh } from 'ContentEditorUI/redux/selectors/inpageSelectors';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getIsUngatedForPatagonia } from 'ContentEditorUI/redux/selectors/authSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledBreakpointControls = styled(EPBreakpointControls).withConfig({
  displayName: "StyleBreakPointsControlsContainer__StyledBreakpointControls",
  componentId: "swnld4-0"
})([".breakpoint-buttons{font-size:20px;}"]);
const StyleBreakPointsControlsContainer = ({
  className = '',
  onChange = emptyFunction,
  toggleModuleEditSidebarCallback
}) => {
  const dispatch = useDispatch();
  const activeBreakpointPreviewWidth = useSelector(getActiveBreakpointPreviewWidth);
  const activeSidebarPanel = useSelector(getActiveSidebarPanel);
  const breakpoints = useSelector(getResponsiveBreakpoints);
  const isUngatedForPatagonia = useSelector(getIsUngatedForPatagonia);
  const isInDefaultBreakpointMode = useSelector(getIsInDefaultBreakpointMode);
  const previewIframeNeedsRefresh = useSelector(getPreviewIframeNeedsRefresh);
  function getLabelForBreakpoint(breakpointName) {
    const builtInBreakpointLabels = {
      default: 'components.header.deviceStylingControls.desktop',
      mobile: 'components.header.deviceStylingControls.mobile'
    };
    if (builtInBreakpointLabels[breakpointName]) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: builtInBreakpointLabels[breakpointName]
      });
    } else {
      // custom breakpoint, no translated label for this
      // TODO: Figure out how we want to provide translated labels, was punted on for POC
      return /*#__PURE__*/_jsx(Fragment, {
        children: breakpointName
      });
    }
  }
  function getIconForBreakpoint(breakpointName) {
    // Temporary icon logic, this will probably get reimagined before we ship
    // breakpoint styles. This is mostly to stop warnings in console about
    // not using valid icon names.
    const fallbackIcon = 'tablet';
    const iconName = breakpointName === 'default' ? 'desktop' : breakpointName;
    if (iconNames.indexOf(iconName) > -1) {
      return iconName;
    }
    return fallbackIcon;
  }
  function getFormattedBreakpoints() {
    return breakpoints.map(breakpoint => {
      const {
        name
      } = breakpoint;
      return {
        name,
        icon: getIconForBreakpoint(name),
        tooltip: getLabelForBreakpoint(name)
      };
    });
  }
  const handleBreakpointSelect = e => {
    if (previewIframeNeedsRefresh && isInDefaultBreakpointMode && activeSidebarPanel === SIDEBAR_PANELS.edit && toggleModuleEditSidebarCallback) {
      // This is currently used to call apply on ModuleEditCMv2, so that content changes are
      // picked up on the page before switching to mobile editing mode
      toggleModuleEditSidebarCallback();
    }
    const selectedBreakpoint = e.target.value === 'desktop' ? 'default' : e.target.value;
    const activeBreakpoint = breakpoints.find(breakpoint => breakpoint.name === selectedBreakpoint);
    dispatch(selectBreakpoint(activeBreakpoint, isUngatedForPatagonia));
    onChange(e);
    UsageTracker.trackEditorInteraction({
      action: 'Switch breakpoints',
      event: selectedBreakpoint
    });
  };
  return /*#__PURE__*/_jsx(StyledBreakpointControls, {
    breakpoints: getFormattedBreakpoints(),
    buttonSize: isUngatedForPatagonia ? 'medium' : 'small',
    buttonClassName: isUngatedForPatagonia ? 'p-all-2 breakpoint-buttons' : '',
    className: className,
    onBreakpointSelect: handleBreakpointSelect,
    previewWidth: isUngatedForPatagonia ? activeBreakpointPreviewWidth : null
  });
};
StyleBreakPointsControlsContainer.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  toggleModuleEditSidebarCallback: PropTypes.func
};
export default StyleBreakPointsControlsContainer;